import React, { useState, useEffect } from 'react';
import './AdminOrderDetails.css';
import LoadingSpinner from '../../components/Common/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://transcontinentalnetwork.com/transcontinental-api';

const getAuthToken = () => {
  const token = localStorage.getItem('access_token');
  return token;
};

const AdminOrderDetails = ({ orderId, orderNumber, setCurrentPage, onBack }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (orderNumber) {
      fetchOrderDetails();
    } else if (orderId) {
      // If only orderId is provided, we need to fetch orders list first
      fetchOrderByIdFallback();
    }
  }, [orderId, orderNumber]);

  const fetchOrderDetails = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/orders/${orderNumber}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setOrder(data.data.order);
      } else {
        setError(data.message || 'Failed to fetch order details');
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError('Failed to load order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderByIdFallback = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();

      if (!token) {
        setError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }

      // Get all orders and find the one with matching ID
      const response = await fetch(`${API_BASE_URL}/orders`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();

      if (response.ok && data.success) {
        const foundOrder = data.data.orders.find(o => o.id === orderId);
        if (foundOrder) {
          // Now get full details using order number
          const detailsResponse = await fetch(`${API_BASE_URL}/orders/${foundOrder.order_number}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          });

          const detailsData = await detailsResponse.json();
          if (detailsResponse.ok && detailsData.success) {
            setOrder(detailsData.data.order);
          } else {
            setError('Failed to fetch order details');
          }
        } else {
          setError('Order not found');
        }
      } else {
        setError(data.message || 'Failed to fetch order details');
      }
    } catch (err) {
      console.error('Error fetching order details:', err);
      setError('Failed to load order details. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setStatusLoading(true);
    setError(null);

    try {
      const token = getAuthToken();

      if (!token) {
        setError('Authentication required. Please log in.');
        setStatusLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/orders/${orderId}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          status: newStatus,
          notes: `Status changed to ${newStatus} by admin`
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage(`Order status updated to ${newStatus}`);
        setTimeout(() => setSuccessMessage(''), 3000);
        // Refresh order data
        if (orderNumber) {
          fetchOrderDetails();
        } else {
          fetchOrderByIdFallback();
        }
      } else {
        setError(data.message || 'Failed to update order status');
      }
    } catch (err) {
      console.error('Error updating order status:', err);
      setError('Failed to update order status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handlePrintOrder = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'N/A';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  const formatPrice = (price) => {
    const numPrice = parseFloat(price) || 0;
    return `$${numPrice.toFixed(2)}`;
  };

  const getStatusClass = (status) => {
    const statusClasses = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      processing: 'status-processing',
      shipped: 'status-shipped',
      delivered: 'status-delivered',
      cancelled: 'status-cancelled'
    };
    return statusClasses[status?.toLowerCase()] || 'status-pending';
  };

  if (loading) {
    return (
      <div className="order-details-container">
        <LoadingSpinner type="page" message="Loading order details..." />
      </div>
    );
  }

  if (error && !order) {
    return (
      <div className="order-details-container">
        <div className="error-state">
          <p>⚠️ {error}</p>
          <button className="btn-back" onClick={onBack}>
            ← Back to Orders
          </button>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details-container">
        <div className="error-state">
          <p>📭 Order not found</p>
          <button className="btn-back" onClick={onBack}>
            ← Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-details-container">
      {/* Header */}
      <div className="details-header">
        <button className="btn-back-inline" onClick={onBack}>
          ← Back to Orders
        </button>
        <div className="header-content">
          <h1>Order Details</h1>
          <p>Order #{order.order_number}</p>
        </div>
        <button className="btn-print" onClick={handlePrintOrder}>
          🖨️ Print
        </button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="success-message">
          ✓ {successMessage}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="error-message">
          ⚠️ {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Order Summary */}
      <div className="order-summary-section">
        <div className="summary-card">
          <h2>Order Information</h2>
          <div className="summary-grid">
            <div className="summary-item">
              <label>Order Number:</label>
              <span className="order-number">{order.order_number}</span>
            </div>
            <div className="summary-item">
              <label>Order Date:</label>
              <span>{formatDate(order.timestamps?.created_at || order.created_at)}</span>
            </div>
            <div className="summary-item">
              <label>Status:</label>
              <span className={`status-badge ${getStatusClass(order.status)}`}>
                {order.status}
              </span>
            </div>
            <div className="summary-item">
              <label>Total Amount:</label>
              <span className="total-amount">
                {formatPrice(order.total_amount || order.pricing?.total_amount || order.totals?.total || order.total || 0)}
              </span>
            </div>
            <div className="summary-item">
              <label>Payment Method:</label>
              <span>{(order.payment_method || 'COD').toUpperCase()}</span>
            </div>
            <div className="summary-item">
              <label>Payment Status:</label>
              <span>{order.payment_status || 'Pending'}</span>
            </div>
          </div>

          {/* Status Update */}
          <div className="status-update-section">
            <label>Update Order Status:</label>
            <div className="status-buttons">
              <button
                className={`status-btn ${order.status?.toLowerCase() === 'pending' ? 'active' : ''}`}
                onClick={() => handleStatusChange('pending')}
                disabled={order.status?.toLowerCase() === 'pending' || statusLoading}
              >
                Pending
              </button>
              <button
                className={`status-btn ${order.status?.toLowerCase() === 'confirmed' ? 'active' : ''}`}
                onClick={() => handleStatusChange('confirmed')}
                disabled={order.status?.toLowerCase() === 'confirmed' || statusLoading}
              >
                Confirmed
              </button>
              <button
                className={`status-btn ${order.status?.toLowerCase() === 'processing' ? 'active' : ''}`}
                onClick={() => handleStatusChange('processing')}
                disabled={order.status?.toLowerCase() === 'processing' || statusLoading}
              >
                Processing
              </button>
              <button
                className={`status-btn ${order.status?.toLowerCase() === 'shipped' ? 'active' : ''}`}
                onClick={() => handleStatusChange('shipped')}
                disabled={order.status?.toLowerCase() === 'shipped' || statusLoading}
              >
                Shipped
              </button>
              <button
                className={`status-btn ${order.status?.toLowerCase() === 'delivered' ? 'active' : ''}`}
                onClick={() => handleStatusChange('delivered')}
                disabled={order.status?.toLowerCase() === 'delivered' || statusLoading}
              >
                Delivered
              </button>
              <button
                className={`status-btn cancel ${order.status?.toLowerCase() === 'cancelled' ? 'active' : ''}`}
                onClick={() => handleStatusChange('cancelled')}
                disabled={order.status?.toLowerCase() === 'cancelled' || statusLoading}
              >
                Cancelled
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Customer & Shipping Info */}
      <div className="info-sections">
        <div className="info-card">
          <h2>Customer Information</h2>
          <div className="info-content">
            <p><strong>Name:</strong> {order.customer_name || order.customer_info?.name || order.customer_info?.fullName || 'N/A'}</p>
            <p><strong>Email:</strong> {order.customer_email || order.customer_info?.email || 'N/A'}</p>
            <p><strong>Phone:</strong> {order.customer_phone || order.customer_info?.phone || 'N/A'}</p>
            {(order.customer_company || order.customer_info?.company) && (
              <p><strong>Company:</strong> {order.customer_company || order.customer_info?.company}</p>
            )}
            {order.is_guest_order && (
              <p className="guest-badge">👤 <strong>Guest Order</strong></p>
            )}
            {order.user_id && (
              <p><strong>User ID:</strong> {order.user_id}</p>
            )}
          </div>
        </div>

        <div className="info-card">
          <h2>Shipping Address</h2>
          <div className="info-content">
            {order.shipping_address ? (
              (() => {
                // Handle JSON string
                if (typeof order.shipping_address === 'string') {
                  try {
                    const addr = JSON.parse(order.shipping_address);
                    return (
                      <>
                        <p>{addr.address || 'N/A'}</p>
                        <p>{[addr.city, addr.state].filter(Boolean).join(', ')}</p>
                        <p>{addr.zipCode || ''}</p>
                        <p>{addr.country || 'Pakistan'}</p>
                      </>
                    );
                  } catch (e) {
                    return <p>{order.shipping_address}</p>;
                  }
                }
                // Handle object
                return (
                  <>
                    <p>{order.shipping_address.address || 'N/A'}</p>
                    <p>{[order.shipping_address.city, order.shipping_address.state].filter(Boolean).join(', ')}</p>
                    <p>{order.shipping_address.zipCode || ''}</p>
                    <p>{order.shipping_address.country || 'Pakistan'}</p>
                  </>
                );
              })()
            ) : (
              <p>No shipping address available</p>
            )}
          </div>
        </div>
      </div>

      {/* Order Items */}
      <div className="items-section">
        <h2>Order Items ({order.item_count || order.items?.length || 0})</h2>
        <div className="items-table-container">
          <table className="items-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Price</th>
                <th>Quantity</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items && order.items.length > 0 ? (
                order.items.map((item, index) => (
                  <tr key={index}>
                    <td>
                      <div className="item-info">
                        {(item.item_details?.image || item.image) && (
                          <img
                            src={item.item_details?.image || item.image}
                            alt={item.item_name || item.title || item.name || 'Product'}
                            className="item-image"
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                        <div>
                          <strong>{item.item_name || item.title || item.name || 'Product'}</strong>
                          {item.item_details?.category && (
                            <span className="item-category">{item.item_details.category}</span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{item.item_sku || item.sku || 'N/A'}</td>
                    <td>{formatPrice(item.unit_price || item.price || 0)}</td>
                    <td>{item.quantity || 1}</td>
                    <td className="item-total">{formatPrice(item.total_price || (item.unit_price || item.price || 0) * (item.quantity || 1))}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">No items found</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4" className="text-right"><strong>Subtotal:</strong></td>
                <td className="total-amount">
                  <strong>{formatPrice(order.subtotal || order.pricing?.subtotal || order.totals?.subtotal || 0)}</strong>
                </td>
              </tr>
              {(order.tax_amount || order.pricing?.tax_amount || order.totals?.tax || 0) > 0 && (
                <tr>
                  <td colSpan="4" className="text-right"><strong>Tax:</strong></td>
                  <td>{formatPrice(order.tax_amount || order.pricing?.tax_amount || order.totals?.tax || 0)}</td>
                </tr>
              )}
              {(order.shipping_cost || order.pricing?.shipping_amount || order.totals?.shipping || 0) > 0 && (
                <tr>
                  <td colSpan="4" className="text-right"><strong>Shipping:</strong></td>
                  <td>{formatPrice(order.shipping_cost || order.pricing?.shipping_amount || order.totals?.shipping || 0)}</td>
                </tr>
              )}
              {(order.discount_amount || order.pricing?.discount_amount || order.totals?.discount || 0) > 0 && (
                <tr>
                  <td colSpan="4" className="text-right"><strong>Discount:</strong></td>
                  <td>-{formatPrice(order.discount_amount || order.pricing?.discount_amount || order.totals?.discount || 0)}</td>
                </tr>
              )}
              <tr className="total-row">
                <td colSpan="4" className="text-right"><strong>Total:</strong></td>
                <td className="total-amount">
                  <strong>{formatPrice(order.total_amount || order.pricing?.total_amount || order.totals?.total || order.total || 0)}</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Additional Information */}
      {(order.delivery_info?.preference || order.delivery_info?.special_instructions || order.notes?.order_notes) && (
        <div className="additional-info-section">
          <h2>Additional Information</h2>
          <div className="info-content">
            {order.delivery_info?.preference && (
              <p><strong>Delivery Time Preference:</strong> {order.delivery_info.preference}</p>
            )}
            {order.delivery_info?.estimated_delivery_date && (
              <p><strong>Estimated Delivery:</strong> {formatDate(order.delivery_info.estimated_delivery_date)}</p>
            )}
            {order.delivery_info?.special_instructions && (
              <div>
                <p><strong>Special Instructions:</strong></p>
                <p className="instruction-text">{order.delivery_info.special_instructions}</p>
              </div>
            )}
            {order.notes?.order_notes && (
              <div>
                <p><strong>Order Notes:</strong></p>
                <p className="instruction-text">{order.notes.order_notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin Notes Section */}
      <div className="notes-section">
        <h2>Admin Notes</h2>

        {order.notes?.admin_notes ? (
          <div className="admin-notes-display">
            <div className="note-item">
              <p>{order.notes.admin_notes}</p>
            </div>
          </div>
        ) : (
          <p className="no-notes">📝 No admin notes yet</p>
        )}

        <div className="note-info">
          <p className="text-muted">
            <strong>Note:</strong> To add or edit admin notes, you would need to implement a backend endpoint
            at <code>POST /api/orders/{'{id}'}/notes</code>
          </p>
        </div>
      </div>

      {/* Status History */}
      {order.status_history && order.status_history.length > 0 && (
        <div className="status-history-section">
          <h2>Status History</h2>
          <div className="history-list">
            {order.status_history.map((history, index) => (
              <div key={index} className="history-item">
                <div className="history-status">
                  <span className={`status-badge ${getStatusClass(history.new_status)}`}>
                    {history.old_status} → {history.new_status}
                  </span>
                </div>
                <div className="history-details">
                  {history.notes && <p>{history.notes}</p>}
                  <div className="history-meta">
                    <small>🕒 {formatDate(history.changed_at)}</small>
                    {history.changed_by && <small> • 👤 {history.changed_by}</small>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Order Metadata */}
      {order.metadata && Object.keys(order.metadata).length > 0 && (
        <div className="metadata-section">
          <h2>Order Metadata</h2>
          <div className="info-content">
            {order.metadata.ip_address && (
              <p><strong>IP Address:</strong> {order.metadata.ip_address}</p>
            )}
            {order.metadata.user_agent && (
              <p><strong>User Agent:</strong> {order.metadata.user_agent}</p>
            )}
            {order.metadata.source && (
              <p><strong>Source:</strong> {order.metadata.source}</p>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="details-footer">
        <button className="btn-back" onClick={onBack}>
          ← Back to Orders
        </button>
        <button className="btn-print" onClick={handlePrintOrder}>
          🖨️ Print Order
        </button>
      </div>
    </div>
  );
};

export default AdminOrderDetails;